import { Controller } from "@hotwired/stimulus"
import { assignRank } from "../helpers/rank_order.js"
import { findStepElement } from "../helpers/dom.js"

// Ranks endings inside one item. Practice mode never writes an answer.
export default class extends Controller {
  static targets = ["step", "backButton", "nextButton", "submitButton", "progressText", "progressBar", "form"]
  static values = {
    total: { type: Number, default: 1 },
    initialStep: { type: Number, default: 1 },
    autosaveUrl: { type: String, default: "" },
    mode: { type: String, default: "order" },
    values: Array,
    labels: { type: Object, default: {} },
    practice: { type: Boolean, default: false }
  }

  connect() {
    this.currentStep = this.initialStepValue > 0 ? this.initialStepValue : 1
    this.formSubmitted = false
    if (this.practiceValue) {
      this.paint(this.element)
      return
    }
    this.stepTargets.forEach((step) => this.paint(step))
    this.render()
  }

  pick(event) {
    event.preventDefault()
    const scope = this.scopeFor(event.currentTarget)
    if (!scope || this.locked(scope)) return
    const option = event.currentTarget.dataset.option
    if (!option) return
    const ranks = readRanks(scope)
    if (Object.prototype.hasOwnProperty.call(ranks, option)) return
    const next = assignRank(this.modeValue, this.valuesValue, Object.values(ranks))
    if (next == null) return
    ranks[option] = next
    this.fillRemainder(scope, ranks)
    writeRanks(scope, ranks)
    this.paint(scope)
    if (this.practiceValue || !isComplete(scope, this.valuesValue)) return
    this.persist(scope)
    this.render()
  }

  reset(event) {
    event.preventDefault()
    const scope = this.scopeFor(event.currentTarget)
    if (!scope) return
    writeRanks(scope, {})
    this.paint(scope)
    if (!this.practiceValue) this.render()
  }

  next(event) {
    event?.preventDefault()
    if (this.practiceValue) return
    const step = findStepElement(this.stepTargets, this.currentStep)
    if (!step || !isComplete(step, this.valuesValue)) return
    if (this.currentStep >= this.totalValue) return
    this.currentStep += 1
    this.render()
  }

  back(event) {
    event?.preventDefault()
    if (this.practiceValue || this.currentStep <= 1) return
    this.currentStep -= 1
    this.render()
  }

  onSubmit(event) {
    if (this.practiceValue) {
      event.preventDefault()
      return
    }
    const ready = this.stepTargets.every((step) => isComplete(step, this.valuesValue))
    if (!ready || this.formSubmitted) {
      event.preventDefault()
      return
    }
    this.formSubmitted = true
  }

  fillRemainder(scope, ranks) {
    if (this.modeValue !== "most_least") return
    const missing = [...scope.querySelectorAll("[data-option]")].filter((button) => {
      return !Object.prototype.hasOwnProperty.call(ranks, button.dataset.option)
    })
    if (missing.length !== 1) return
    const next = assignRank(this.modeValue, this.valuesValue, Object.values(ranks))
    if (next == null) return
    ranks[missing[0].dataset.option] = next
  }

  persist(scope) {
    if (!this.hasAutosaveUrlValue || !this.autosaveUrlValue) return
    const questionNumber = scope.dataset.questionNumber
    const field = scope.querySelector("[data-rank-field]")
    if (!questionNumber || !field?.value) return
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams()
    body.set("question_number", questionNumber)
    body.set("value", field.value)
    try {
      fetch(this.autosaveUrlValue, {
        method: "POST",
        body,
        keepalive: true,
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": token || "",
          Accept: "application/json"
        }
      }).catch(() => {})
    } catch (_error) {
      // autosave must not block the next item
    }
  }

  render() {
    this.stepTargets.forEach((step) => {
      const open = Number(step.dataset.step) === this.currentStep
      step.hidden = !open
    })
    if (this.hasProgressTextTarget) this.progressTextTarget.textContent = this.progressLabel
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.value = this.currentStep
      this.progressBarTarget.setAttribute("aria-valuenow", String(this.currentStep))
    }
    const step = findStepElement(this.stepTargets, this.currentStep)
    const ready = step ? isComplete(step, this.valuesValue) : false
    const last = this.currentStep >= this.totalValue
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.hidden = this.currentStep <= 1
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.hidden = last
      this.nextButtonTarget.disabled = !ready
    }
    if (this.hasSubmitButtonTarget) {
      const allReady = this.stepTargets.every((item) => isComplete(item, this.valuesValue))
      this.submitButtonTarget.hidden = !last
      this.submitButtonTarget.disabled = !allReady
    }
  }

  scopeFor(node) {
    if (this.practiceValue) return this.element
    return node?.closest("[data-rank-within-item-target='step']")
  }

  locked(scope) {
    return !this.practiceValue && isComplete(scope, this.valuesValue)
  }

  paint(scope) {
    const ranks = readRanks(scope)
    scope.querySelectorAll("[data-option]").forEach((button) => {
      const rank = ranks[button.dataset.option]
      const mark = button.querySelector("[data-rank-mark]")
      if (mark) mark.textContent = rank == null ? "" : this.markText(rank)
      button.classList.toggle("is-primary", rank != null)
      button.classList.toggle("is-light", rank == null)
      button.setAttribute("aria-pressed", rank == null ? "false" : "true")
    })
  }

  markText(rank) {
    const labels = this.hasLabelsValue ? this.labelsValue : {}
    return labels[String(rank)] || String(rank)
  }

  get progressLabel() {
    const template = this.element.querySelector("[data-rank-within-item-progress-template]")
    if (template) {
      return template.textContent.replace("%{current}", this.currentStep).replace("%{total}", this.totalValue)
    }
    return `${this.currentStep} / ${this.totalValue}`
  }
}

function readRanks(scope) {
  const field = scope.querySelector("[data-rank-field]")
  if (!field?.value) return {}
  try {
    const parsed = JSON.parse(field.value)
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch (_error) {
    return {}
  }
}

function writeRanks(scope, ranks) {
  const field = scope.querySelector("[data-rank-field]")
  if (!field) return
  field.value = Object.keys(ranks).length === 0 ? "" : JSON.stringify(ranks)
}

function isComplete(scope, values) {
  const ranks = readRanks(scope)
  const got = Object.values(ranks).map(Number)
  const want = [...new Set(values.map(Number))]
  if (got.length !== want.length) return false
  return want.every((value) => got.includes(value)) && new Set(got).size === got.length
}
